/**
 * SPCChart: a control chart.
 *
 * Given the server's series (`series`, from `GET /v1/twin/spc/charts/{id}/series`) it draws the points (a null
 * value is a gap: the line breaks there), the center line and control limits as the server set them at every
 * point (step lines: a rate chart's limits move with each day's castings), the baseline window shaded, points
 * before the baseline muted, values at the sensor's ceiling or floor with their own marker, and the points that
 * broke a rule in red, the rules named in the tooltip. The header says how the limits were set and which version
 * is in force, and flags provisional limits and an unstable baseline. An EWMA or CUSUM chart plots its statistic.
 *
 * Nothing is estimated here: without a series only explicit `centerline`, `ucl` and `lcl` draw limits, and with
 * none the chart plots the points and says "No control limits".
 */

import { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { spcLimitsHiddenText, type SpcSeries } from "@/lib/api/spc";
import { cn } from "@/lib/ui/cn";
import { ChartSkeleton } from "../ChartSkeleton";
import { yAxisDomain } from "../timeseries/TimeseriesChart";
import type { WidgetState } from "../types";
import { spcPointsPlot, spcSeriesPlot, spcTimeAxis } from "./spc/plot";
import { SpcChartHeader } from "./spc/SpcChartHeader";
import { SpcDot, SpcTooltip } from "./spc/SpcMarkers";
import { SERIES_PALETTE } from "../series-palette";

export interface SPCPoint {
  /** X-axis label (date, batch number, etc.). */
  x: string | number;
  /** Measured value. */
  v: number;
}

export interface SPCChartProps {
  /** Plain points, drawn when there is no `series`. */
  points?: SPCPoint[];
  /** The server's control chart: points, the limits at every point, the baseline window, the signals. */
  series?: SpcSeries;
  /** Explicit center line and control limits for plain `points`: all three, or no limits are drawn. */
  centerline?: number;
  ucl?: number;
  lcl?: number;
  /**
   * Compare-with-ideal overlay: a frozen "golden" reference (mean + population σ over a foundry-chosen good
   * window) drawn distinctly from the control limits: a solid accent line + ±σ bands. `bands` = how many ±σ
   * bands to shade (default 2). See docs/versatile/specs/data-dashboards-qc.md §3.5.
   */
  reference?: { mean: number; sigma: number; bands?: 1 | 2 | 3 };
  yLabel?: string;
  unit?: string;
  /**
   * Fixed lower bound for the value (Y) axis. Omit for autorange (the default: fits the points and the
   * limits). When both bounds are set the axis clips to the window rather than expanding for outliers.
   */
  yMin?: number;
  /** Fixed upper bound for the value (Y) axis. Omit for autorange. */
  yMax?: number;
  height?: number;
  state?: WidgetState;
  className?: string;
}

const NO_POINTS: SPCPoint[] = [];
/** Above this many points only signals and censored values get a marker. */
const DENSE = 150;
const MUTED = "rgb(var(--color-on-surface-muted))";
const DANGER = "rgb(var(--color-danger))";
const LIMIT_LINES = [
  { key: "center", stroke: MUTED, dash: "3 3" },
  { key: "ucl", stroke: DANGER, dash: "4 3" },
  { key: "lcl", stroke: DANGER, dash: "4 3" },
] as const;

export function SPCChart({
  points = NO_POINTS,
  series,
  centerline,
  ucl,
  lcl,
  reference,
  yLabel,
  unit,
  yMin,
  yMax,
  height = 280,
  state = { kind: "ok" },
  className,
}: SPCChartProps) {
  const yAxis = yAxisDomain(yMin, yMax);
  const plot = useMemo(() => {
    if (series) return spcSeriesPlot(series);
    const given = centerline !== undefined && ucl !== undefined && lcl !== undefined;
    return spcPointsPlot(points, given ? { center: centerline, ucl, lcl } : undefined);
  }, [series, points, centerline, ucl, lcl]);

  if (state.kind === "loading") {
    return <ChartSkeleton shape="spc" height={height} className={className} />;
  }
  const rows = plot.rows;
  if (state.kind === "empty" || !rows.some((r) => r.v !== null || r.vPre !== null)) {
    return (
      <div
        className={cn(
          "flex w-full items-center justify-center rounded-md border border-dashed border-border bg-surface-elevated/40 text-sm text-on-surface-muted",
          className,
        )}
        style={{ height }}
      >
        {series ? "No readings in this window." : "No readings to chart."}
      </div>
    );
  }

  const dense = rows.length > DENSE;
  const bands = reference?.bands ?? 2;
  // A port's limits in another unit than its points are hidden, and the chart says why.
  const hiddenWhy = spcLimitsHiddenText(series);
  return (
    <div className={cn("flex w-full flex-col gap-1", className)} style={{ height }}>
      <SpcChartHeader series={series} plot={plot} reference={Boolean(reference)} limitsHidden={hiddenWhy} />
      {hiddenWhy && <p className="shrink-0 text-[11px] text-on-surface-muted">{hiddenWhy}</p>}
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 8 }}>
            <CartesianGrid stroke="rgb(var(--color-border))" strokeDasharray="3 3" vertical={false} />
            {/* The baseline window: the points the limits were estimated from. */}
            {plot.band && (
              <ReferenceArea
                x1={plot.band.x1}
                x2={plot.band.x2}
                fill="rgb(var(--color-info))"
                fillOpacity={0.08}
                stroke="none"
              />
            )}
            {/* Compare-with-ideal: the frozen golden reference, ±σ bands (widest first) + a solid accent line,
                distinct from the dashed control limits. */}
            {reference &&
              Array.from({ length: bands }, (_, i) => bands - i).map((k) => (
                <ReferenceArea
                  key={`ref-band-${k}`}
                  y1={reference.mean - k * Math.abs(reference.sigma)}
                  y2={reference.mean + k * Math.abs(reference.sigma)}
                  fill="rgb(var(--color-accent))"
                  fillOpacity={0.06}
                  stroke="none"
                />
              ))}
            {reference && (
              <ReferenceLine
                y={reference.mean}
                stroke="rgb(var(--color-accent))"
                strokeWidth={1.5}
                label={{ value: "ideal", position: "left", fontSize: 10, fill: "rgb(var(--color-accent))" }}
              />
            )}
            <XAxis dataKey="x" stroke={MUTED} tick={{ fontSize: 10 }} tickMargin={6} {...spcTimeAxis(plot)} />
            <YAxis
              stroke={MUTED}
              tick={{ fontSize: 11 }}
              domain={yAxis.domain}
              allowDataOverflow={yAxis.allowDataOverflow}
              label={
                yLabel
                  ? {
                      value: unit ? `${yLabel} (${unit})` : yLabel,
                      angle: -90,
                      position: "insideLeft",
                      style: { textAnchor: "middle", fontSize: 11, fill: MUTED },
                    }
                  : undefined
              }
            />
            <Tooltip content={<SpcTooltip kind={plot.kind} unit={unit} />} />
            {plot.hasLimits &&
              LIMIT_LINES.map((l) => (
                <Line
                  key={l.key}
                  dataKey={l.key}
                  type="stepAfter"
                  stroke={l.stroke}
                  strokeDasharray={l.dash}
                  strokeWidth={1}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              ))}
            {plot.hasPre && (
              <Line
                dataKey="vPre"
                stroke={MUTED}
                strokeOpacity={0.6}
                strokeWidth={1.25}
                connectNulls={false}
                isAnimationActive={false}
                dot={<SpcDot mode={dense ? "dense" : "muted"} />}
              />
            )}
            {plot.hasLower && (
              <Line
                dataKey="lower"
                stroke={SERIES_PALETTE[1]}
                strokeWidth={1.5}
                connectNulls={false}
                isAnimationActive={false}
                dot={false}
              />
            )}
            <Line
              dataKey="v"
              stroke={SERIES_PALETTE[0]}
              strokeWidth={1.5}
              connectNulls={false}
              isAnimationActive={false}
              dot={<SpcDot mode={dense ? "dense" : "plain"} />}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
